import type { ReactNode } from 'react'
import { Link, Outlet, useRouterState } from '@tanstack/react-router'
import type { LucideIcon } from 'lucide-react'

import { cn } from '@/lib/utils'

export interface SubTab {
  path: string
  label: string
  Icon: LucideIcon
  /** true면 정확히 이 경로일 때만 active (index 탭용). 아니면 startsWith. */
  matchExact?: boolean
  hint?: string
}

interface Props {
  Icon: LucideIcon
  title: string
  subtitle: string
  subtabs: SubTab[]
}

/** 헤더(아이콘/제목/설명) + 하위 탭 내비 + Outlet 공용 shell.
 *  견적서 변환(QuoteShell)·발주서 변환(ConvertShell) 등 sub-tab 묶음에 사용.
 */
export function SubTabShell({ Icon, title, subtitle, subtabs }: Props): ReactNode {
  const path = useRouterState({ select: (s) => s.location.pathname })

  return (
    <div className="flex flex-col h-full">
      <header className="px-6 pt-6 pb-3 flex items-center gap-3">
        <Icon className="w-6 h-6 text-emerald-600"/>
        <div>
          <h2 className="text-2xl font-bold text-slate-900 tracking-tight">{title}</h2>
          <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>
        </div>
      </header>

      <nav className="px-6 border-b border-slate-200 bg-white sticky top-0 z-10">
        <div className="flex gap-1 -mb-px">
          {subtabs.map(({ path: p, label, Icon: TabIcon, matchExact, hint }) => {
            const active = matchExact ? path === p : path.startsWith(p)
            return (
              <Link
                key={p}
                to={p}
                title={hint}
                className={cn(
                  'inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors',
                  active
                    ? 'border-emerald-600 text-emerald-700'
                    : 'border-transparent text-slate-500 hover:text-slate-800 hover:border-slate-300',
                )}
              >
                <TabIcon className="w-4 h-4"/>
                {label}
              </Link>
            )
          })}
        </div>
      </nav>

      <div className="flex-1 overflow-y-auto">
        <Outlet />
      </div>
    </div>
  )
}
